import { Navigate } from 'react-router'
import { useFormConfirmed } from 'shared/hooks/useFormConfirmed'

import { SuccessData, FormCollateral } from 'modules/pools/ui/FormCollateral'
import { SentTransaction } from 'modules/pools/ui/SentTransaction'
import { NarrowWrapper } from 'shared/ui/layout/NarrowWrapper'
import { PageLoader } from 'shared/ui/layout/PageLoader'

import { createRoute } from 'modules/router/utils/createRoute'
import { PoolAsset } from 'modules/pools/constants/poolAssets'
import { useQueryParams } from 'modules/router/hooks/useQueryParams'
import { useCollateralData } from 'modules/pools/hooks/useCollateralData'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import {
  ContractCollateralManager,
  ContractNFCS,
  ContractRociPayment,
} from 'modules/contracts/contracts'
import { CollateralLockCode } from 'modules/pools/constants/lockCodes'
import { useSWR } from 'modules/network/hooks/useSwr'

function RouteCollateral() {
  const { walletAddress, chainId } = useWeb3()
  const { action = '' } = useQueryParams()
  const { pathname, isConfirmed, successData, confirmForm } =
    useFormConfirmed<SuccessData>()

  const { data, isLoading, isValidating } = useCollateralData()

  const { data: lockCode, isLoading: isLockDataLoading } = useSWR<
    CollateralLockCode | undefined
  >(
    walletAddress ? `${walletAddress}-collateral-route-lock-code` : null,
    async () => {
      if (!walletAddress) return
      const contractNFCS = ContractNFCS.connectRpc({ chainId })
      const contractCollateralManager = ContractCollateralManager.connectRpc({
        chainId,
      })
      const contractRociPayment = ContractRociPayment.connectRpc({ chainId })

      const [
        isNFCSPaused,
        isCollateralManagerPaused,
        isRociPaymentPaused,
        primaryBundleAddress,
      ] = await Promise.all([
        contractNFCS.paused(),
        contractCollateralManager.paused(),
        contractRociPayment.paused(),
        contractNFCS.getPrimaryAddress(walletAddress),
      ])

      if (isNFCSPaused || isCollateralManagerPaused || isRociPaymentPaused) {
        return 'protocolPaused'
      }

      if (walletAddress !== primaryBundleAddress) {
        return 'nonPrimary'
      }
    },
  )

  const collateralData = data ? data[PoolAsset.WETH] : null

  if (!collateralData || isLoading) {
    return <PageLoader />
  }

  if (isConfirmed) {
    if (!successData) return <Navigate replace to={pathname} />
    return (
      <SentTransaction
        transactionType={
          successData.formValues.action === 'add'
            ? 'Collateral refill'
            : 'Collateral withdrawal'
        }
        txHash={successData.tx.hash}
        amountToDisplay={successData.formValues.amount}
        amountToken={successData.formValues.asset}
      />
    )
  }

  return (
    <NarrowWrapper>
      <FormCollateral
        action={action as string}
        collateralAsset={collateralData.asset}
        totalCollateral={collateralData.totalAmount}
        maxCollateralToWithdraw={collateralData.availableAmount}
        isDataLoading={isValidating || isLockDataLoading}
        lockCode={lockCode}
        onSuccess={confirmForm}
      />
    </NarrowWrapper>
  )
}

export const routeCollateral = createRoute({
  headerTitle: 'Collateral',
  layoutType: 'narrow-extended',
  component: RouteCollateral,
  checkConnect: true,
})
